import classes from "./timeline.module.css";
import popupClasses from "./StatusRemarkPopup.module.css";
import "react-widgets/dist/css/react-widgets.css";
import { DateTimePicker } from "react-widgets";
import { useSimConsent } from "../hooks/useSimConsent";
import useFetchLRDetails from "../hooks/useFetchLRDetails";
import { useState } from "react";
import { closeModal } from "../InvoiceV2/utils";

const formatHistoryTimestamp = (inputdate) => {
  const date = new Date(inputdate);
  if (isNaN(date)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const time = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${day}-${month}-${year}, ${time}`;
};

const whoUpdated = (entry) => {
  if (entry.updated_by_name) {
    return entry.updated_by_email
      ? `${entry.updated_by_name} (${entry.updated_by_email})`
      : entry.updated_by_name;
  }
  return entry.updated_by === "company" || entry.updated_by === "client"
    ? "Me"
    : entry.updated_by === "system"
      ? "System"
      : "Transporter";
};

const StatusRemarkPopup = ({
  indent_id,
  indent_id_sub,
  selecteddate,
  setSelectedDate,
  mindate,
  maxdate,
  comments,
  selectedvalue,
  handleCommentChange,
  updatestatus,
  historyEntries = [],
}) => {

  const { hasAccess, parseConsentStatus, SIM_CONSENT_STATUS, vehicleDetails, loading, error } = useSimConsent(indent_id);
  const { details: lrDetails, loading: lrLoading, error: lrError } = useFetchLRDetails({ indent_id, indent_sub_id: indent_id_sub });
  const [showConsignmentWarning, setShowConsignmentWarning] = useState(false);

  const consentStatus = parseConsentStatus(vehicleDetails);

  const handleUpdate = () => {
    if (selectedvalue === "dispatched") {
      const hasConsignmentDetails = lrDetails?.consignment_details?.length > 0;
      if (!hasConsignmentDetails) {
        setShowConsignmentWarning(true);
        return;
      }
    }
    updatestatus();
  };

  return (
    <>
      <div className="row" style={{ margin: "0", padding: "1% 0" }}>
        <h5 style={{ textAlign: "center", width: "100%" }}>
          <span style={{ fontWeight: "600" }}>Indent ID: </span>
          <span style={{ fontWeight: "300" }}>{indent_id_sub}</span>
        </h5>
      </div>

      {historyEntries.length > 1 && (
        <div className={popupClasses.editHistorySection}>
          <div className={popupClasses.editHistoryTitle}>
            <i className="fa fa-history" style={{ marginRight: "6px" }} />
            Previously edited
          </div>
          {historyEntries.map((entry, i) => {
            const isLatest = i === historyEntries.length - 1;
            return (
              <div key={`popup-history-${i}`} className={popupClasses.editHistoryRow}>
                <span
                  className={
                    isLatest
                      ? popupClasses.editHistoryDotLatest
                      : popupClasses.editHistoryDot
                  }
                />
                <span className={popupClasses.editHistoryTime}>
                  {formatHistoryTimestamp(entry.timestamp)}
                </span>
                <span className={popupClasses.editHistoryWho}>
                  {whoUpdated(entry)}
                </span>
                {isLatest && (
                  <span className={popupClasses.editHistoryCurrentTag}>
                    Current
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="row" style={{ margin: "0", padding: "1% 0" }}>
        <span
          style={{ width: "25%", marginTop: "5px", textAlign: "left" }}
        >
          Date and Time:
        </span>
        <DateTimePicker
          value={new Date(selecteddate)}
          step={30}
          onChange={(e) =>
            setSelectedDate(e && !isNaN(e) ? new Date(e) : new Date())
          }
          min={mindate}
          max={maxdate}
          className={classes["datetimepicker"]}
        />
      </div>

      <div className="row" style={{ margin: "0", padding: "1% 0" }}>
        <span
          style={{ width: "25%", marginTop: "5px", textAlign: "left" }}
        >
          Comments:
        </span>
        <input
          style={{ width: "75%" }}
          type="text"
          value={comments[selectedvalue] || ""}
          onChange={(e) => handleCommentChange(e.target.value)}
        />
      </div>

      {hasAccess && !error && selectedvalue === "dispatched" && (
        <div className={popupClasses.simConsentAlert}>
          <div>
            <i className={`fa fa-info-circle ${popupClasses.alertIcon}`}></i>
            SIM tracking will begin from when vehicle is marked as <b>Dispatched</b>.
          </div>
          <div className={popupClasses.consentDetails}>
            Tracking Consent Status:
            {consentStatus === SIM_CONSENT_STATUS.ALLOWED ? (
              <span className={`badge ${popupClasses["badge-success"]} badge-sm mx-3`}><i className="fa fa-check"> Active </i></span>
            ) : consentStatus === SIM_CONSENT_STATUS.PENDING ? (
              <span className={`badge ${popupClasses["badge-warning"]} badge-sm mx-3`}>Pending</span>
            ) : consentStatus === SIM_CONSENT_STATUS.REJECTED ? (
              <span className={`badge ${popupClasses["badge-danger"]} badge-sm mx-3`}>Rejected</span>
            ) : (
              <span className={`badge ${popupClasses["badge-secondary"]} badge-sm mx-3`}>{loading ? "Checking..." : "Not Requested"}</span>
            )}
          </div>
        </div>
      )}

      {showConsignmentWarning ? (
        <div className={popupClasses.warningAlert} role="alert">
          <div>
            <i className={`fa fa-exclamation-triangle ${popupClasses.warningIcon}`}></i>
            Are you sure you want to mark the vehicle as dispatched? You have not entered consignment details.
          </div>
          <div className={popupClasses.alertButtons}>
            <button
              className={`btn btn-sm ${popupClasses.btnSecondary}`}
              onClick={() => {
                setShowConsignmentWarning(false)
                closeModal()
              }}
            >
              Cancel
            </button>
            <button
              className={`btn btn-sm ${popupClasses.btnWarning}`}
              onClick={() => {
                setShowConsignmentWarning(false);
                updatestatus();
              }}
            >
              Proceed
            </button>
          </div>
        </div>
      ) : (
        <div className="d-flex justify-content-end mt-3">
          <button className="btn btn-sm btn-success" onClick={handleUpdate}>
            Update
          </button>
        </div>
      )}
    </>
  )
}

export default StatusRemarkPopup;
